import { pcfRoutes } from '@app/pcf/pcf-routes';
import { soldGoodsRoutes } from '@app/sold-goods/sold-goods-routes';
import { RelatedItem, RelatedItemTypeEnum } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { CardPager } from '@shared/components/dashboard/card-pager';
import { useState } from 'react';
import { generatePath, Link } from 'react-router-dom';
import { ItemPanel } from './item-panel';

const PAGE_SIZE = 3;

const TYPE_LABELS: Record<RelatedItemTypeEnum, string> = {
  product: 'Product',
  sold_good: 'Sold good',
};

function itemHref(item: RelatedItem): string {
  return item.type === RelatedItemTypeEnum.product
    ? generatePath(pcfRoutes.PRODUCT_DETAIL, { product_id: item.uuid })
    : generatePath(soldGoodsRoutes.SOLD_FOOD_AND_DRINK_ITEM, {
        item_uuid: item.uuid,
      });
}

function ItemIcon({ type }: { type: RelatedItemTypeEnum }) {
  const Icon =
    type === RelatedItemTypeEnum.product ? Icons.salad : Icons.shoppingBag;
  return <Icon className="size-3 text-primary-500" />;
}

type Props = {
  items?: readonly RelatedItem[];
};

/**
 * The products and sold goods in the account that use this purchased good.
 * Modelling data, so the list ignores the page's period and site scope.
 */
export function LinkedToPanel({ items = [] }: Props) {
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(items.length / PAGE_SIZE);
  const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <ItemPanel
      title="Linked to"
      footer={
        <CardPager
          page={page}
          pageCount={pageCount}
          onPageChange={setPage}
          className="mt-2 justify-end"
        />
      }
    >
      {items.length === 0 ? (
        <div className="flex h-full min-h-24 items-center justify-center">
          <Text variant="subtle">
            This item is not linked to any products or sold goods in your
            account
          </Text>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {pageItems.map((item) => (
            <li key={`${item.type}-${item.uuid}`}>
              <Link
                to={itemHref(item)}
                className="group flex min-h-16 items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    <ItemIcon type={item.type} />
                    <Text variant="subtle" className="text-xs">
                      {TYPE_LABELS[item.type]}
                    </Text>
                  </div>
                  <Text variant="body" className="truncate">
                    {item.name}
                  </Text>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {item.amount ? (
                    <Text variant="subtle">{item.amount} used</Text>
                  ) : null}
                  <Icons.chevronRight className="size-4 text-teal-600 opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </ItemPanel>
  );
}
